import { memo } from "react";
import { Box, Button, Flex, Image, LinkBox, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { ContentSectionCustomCard } from "../../types";
import { ArrowRightLongIcon } from "../icons/ArrowRightLongIcon";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";

// Breakpoint-specific card configuration (cardWidth and gap only)
// aspectRatio, borderRadius, imageSize are calculated in ChannelButtonContainer based on containerWidth
export const CHANNEL_BUTTON_BREAKPOINT_CONFIG: Record<
  string,
  { cardWidth: number; gap: number }
> = {
  base: { cardWidth: 65, gap: 8 },
  xs: { cardWidth: 65, gap: 8 },
  sm: { cardWidth: 65, gap: 12 },
  md: { cardWidth: 150, gap: 12 },
  lg: { cardWidth: 170, gap: 16 },
  xl: { cardWidth: 190, gap: 16 },
  "2xl": { cardWidth: 207, gap: 16 },
  "3xl": { cardWidth: 230, gap: 16 },
  "4xl": { cardWidth: 280, gap: 16 },
};

// Helper to calculate height from width while maintaining aspect ratio
export const getHeightFromWidth = (width: number, aspectRatio: number = 1.63): number =>
  Math.round(width / aspectRatio);

// Helper to get config for a breakpoint
export const getBreakpointConfig = (breakpoint: string) => {
  return (
    CHANNEL_BUTTON_BREAKPOINT_CONFIG[breakpoint] ||
    CHANNEL_BUTTON_BREAKPOINT_CONFIG["2xl"]
  );
};

interface ChannelButtonCardProps {
  card: ContentSectionCustomCard;
  imageUrl: string | null;
  link?: string;
  isSmall: boolean;
  cardWidth?: number;
  cardHeight?: number;
  borderRadius?: number;
  imageSize?: number;
}

export const ChannelButtonCard = memo(
  ({
    card,
    imageUrl,
    link,
    isSmall,
    cardWidth: propWidth,
    cardHeight: propHeight,
    borderRadius: propBorderRadius = 24,
    imageSize: propImageSize = 60,
  }: ChannelButtonCardProps) => {
    const isMobile = useMobileBreakpoint();

    // Use provided dimensions or fall back to defaults
    const defaultConfig = isSmall
      ? CHANNEL_BUTTON_BREAKPOINT_CONFIG.sm
      : CHANNEL_BUTTON_BREAKPOINT_CONFIG["2xl"];
    const cardWidth = propWidth ?? defaultConfig.cardWidth;
    const cardHeight = propHeight ?? getHeightFromWidth(cardWidth);
    const borderRadius = propBorderRadius;
    const imageSize = propImageSize;

    // Blaze color with 60% opacity for hover effects
    const hoverColor = "#FF5A2C99";

    const defaultBg = "rgba(0, 0, 0, 0.80)";
    const containerGlow = `radial-gradient(69.69% 69.69% at 50% 119.29%, ${hoverColor} 0%, transparent 100%), rgba(0, 0, 0, 0.80)`;

    const glowOverlay = (
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg={containerGlow}
        opacity={0}
        transition="opacity 0.3s ease"
        borderRadius={`${borderRadius}px`}
        _groupHover={{ opacity: 1 }}
      />
    );

    const centerElement = card.center_element;
    const centerText = card.center_text;

    const centerContent = centerElement === "text" ? (
      <Text
        position="relative"
        zIndex={1}
        color="neutral.100"
        fontSize={isMobile ? "10px" : "14px"}
        fontFamily="Bricolage Grotesque"
        fontWeight="600"
        textAlign="center"
        lineHeight="130%"
        noOfLines={2}
        px={2}
      >
        {centerText}
      </Text>
    ) : centerElement === "cta" ? (
      <Flex position="relative" zIndex={1} align="center" justify="center">
        {centerText ? (
          <Button variant="primary" fontSize={isMobile ? "10px" : "13px"}>
            {centerText}
          </Button>
        ) : (
          <Button variant="iconOnly">
            <ArrowRightLongIcon boxSize="24px" />
          </Button>
        )}
      </Flex>
    ) : null;

    const image = imageUrl ? (
      <Box
        position="relative"
        zIndex={1}
        width="100%"
        height={centerContent ? "auto" : "100%"}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex={centerContent ? undefined : 1}
      >
        <Image
          src={imageUrl}
          maxWidth={`${imageSize}%`}
          maxHeight={centerContent ? `${imageSize - 10}%` : `${imageSize}%`}
          objectFit="contain"
          alt={card.display_name || ""}
          transition="filter 0.3s ease"
          _groupHover={{ filter: "drop-shadow(0px 0px 15px #FF5A2CCC) drop-shadow(0px 0px 30px #FF5A2CCC)" }}
        />
      </Box>
    ) : null;

    const content = centerContent ? (
      <Flex
        position="relative"
        zIndex={1}
        direction="column"
        align="center"
        justify="center"
        width="100%"
        height="100%"
        gap={isMobile ? "4px" : "8px"}
      >
        {image}
        {centerContent}
      </Flex>
    ) : image ?? null;

    const sharedProps = {
      width: `${cardWidth}px`,
      height: `${cardHeight}px`,
      borderRadius: `${borderRadius}px`,
      bg: defaultBg,
      border: "1px solid transparent",
      backgroundClip: "padding-box",
      sx: {
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: `${borderRadius}px`,
          padding: "1px",
          background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 100%)",
          mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          maskComposite: "exclude",
          pointerEvents: "none",
        },
      },
      overflow: "visible" as const,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: isMobile ? "8px" : "16px",
      position: "relative" as const,
    };

    if (link) {
      return (
        <LinkBox
          as={RouterLink}
          to={link}
          cursor="pointer"
          role="group"
          {...sharedProps}
        >
          {glowOverlay}
          {content}
        </LinkBox>
      );
    }

    return (
      <Box role="group" {...sharedProps}>
        {glowOverlay}
        {content}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.card.id === nextProps.card.id &&
      prevProps.imageUrl === nextProps.imageUrl &&
      prevProps.link === nextProps.link &&
      prevProps.isSmall === nextProps.isSmall &&
      prevProps.cardWidth === nextProps.cardWidth &&
      prevProps.cardHeight === nextProps.cardHeight &&
      prevProps.borderRadius === nextProps.borderRadius &&
      prevProps.imageSize === nextProps.imageSize
    );
  }
);
